import { useHttpClient } from '@wix/fe-essentials-standalone';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SiteSelectorStateType } from '../components/SiteSelector/SiteSelectorStates';
import { GetUserSitesResponse } from '../services/user/sites';
import type { SiteMetaData } from '../types';

const normalizeCursor = (c: unknown): string | undefined =>
  typeof c === 'string' && c.length > 0 ? c : undefined;

const parseSites = (data: GetUserSitesResponse): SiteMetaData[] => {
  if (!Array.isArray(data?.sites)) {
    throw new Error('Invalid /api/user/sites response');
  }
  return data.sites;
};

const dedup = (prev: SiteMetaData[], next: SiteMetaData[]): SiteMetaData[] => {
  const ids = new Set(prev.map((s) => s.metaSiteId));
  const fresh = next.filter((s) => !ids.has(s.metaSiteId));
  return fresh.length ? [...prev, ...fresh] : prev;
};

export const useUserSitesRemoteSearch = (hookOptions?: {
  enabled?: boolean;
}) => {
  const httpClient = useHttpClient();
  const enabled = hookOptions?.enabled ?? true;
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [sites, setSites] = useState<SiteMetaData[]>([]);
  const [itemsCount, setItemsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [overlay, setOverlay] = useState<SiteSelectorStateType | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const loadingRef = useRef(false);

  const fetchPage = useCallback(
    (term: string, pageCursor?: string) =>
      httpClient
        .get<GetUserSitesResponse>('/api/user/sites', {
          params: { term, cursor: pageCursor },
        })
        .then((res) => res.data),
    [httpClient]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchPage(query)
      .then((data) => {
        if (cancelled) {
          return;
        }
        const next = parseSites(data);
        setOverlay(next.length === 0 ? SiteSelectorStateType.EMPTY : null);
        setSites(next);
        setItemsCount(data.itemsCount || 0);
        setCursor(normalizeCursor(data.cursor));
      })
      .catch(() => {
        if (!cancelled) {
          setOverlay(SiteSelectorStateType.ERROR);
          setSites([]);
          setItemsCount(0);
          setCursor(undefined);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- retryKey forces re-fetch
  }, [enabled, fetchPage, query, retryKey]);

  const hasMore = useMemo(
    () => Boolean(cursor) && (itemsCount === 0 || sites.length < itemsCount),
    [cursor, itemsCount, sites.length]
  );

  const loadMore = useCallback(async () => {
    if (loading || !hasMore || loadingRef.current) {
      return;
    }
    loadingRef.current = true;
    setLoading(true);
    try {
      const data = await fetchPage(query, cursor);
      setSites((prev) => dedup(prev, parseSites(data)));
      setItemsCount(data.itemsCount || 0);
      setCursor(normalizeCursor(data.cursor));
    } catch {
      setOverlay(SiteSelectorStateType.ERROR);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [cursor, fetchPage, hasMore, loading, query]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setOverlay(null);
  }, []);

  const retry = useCallback(() => {
    setOverlay(null);
    setRetryKey((k) => k + 1);
  }, []);

  return {
    query,
    setQuery,
    sites,
    itemsCount,
    loading,
    overlay,
    loadMore,
    hasMore,
    clearSearch,
    retry,
  };
};
